import React, { FC, ReactNode } from 'react';
import { QueryResult, QueryComponentOptions } from 'react-apollo';
import { Subtract } from 'utility-types';

import { LoaderTypes } from 'types/app';
import {
  ClientInvitationsClientList,
  ClientInvitationsClientListInput,
  GetClientInvitationsByClientComponent,
} from 'api';
import { QueryManager } from 'components';

interface ISubtracted {
  query: QueryComponentOptions['query'];
  children: QueryComponentOptions['children'];
}

type TQueryProps = Subtract<QueryComponentOptions, ISubtracted>;

export interface IGetClientInvitationsByClientOptions {
  onLoadMore: () => void;
  exhausted: boolean;
  loading: boolean;
}

interface IProps extends TQueryProps {
  variables: ClientInvitationsClientListInput;
  children: (
    data: ClientInvitationsClientList,
    options: IGetClientInvitationsByClientOptions,
  ) => ReactNode;
  loader?: LoaderTypes;
}

const GetClientInvitationsByClient: FC<IProps> = ({
  children,
  loader,
  variables,
  ...queryProps
}) => {
  function handleLoadMore(queryResult: QueryResult) {
    const { loading, error, data, fetchMore } = queryResult;
    const currentResult = data.getClientInvitationsByClient;

    if (loading || error || !currentResult.id) {
      return Promise.resolve();
    }

    return fetchMore({
      variables: {
        id: currentResult.id,
        limit: variables.limit,
        created_at: currentResult.created_at,
        invitation_status: currentResult.invitation_status,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return prev;
        }

        const prevResult = prev.getClientInvitationsByClient;
        const nextResult = fetchMoreResult.getClientInvitationsByClient;
        const updatedResult = {
          ...prevResult,
          ...nextResult,
          client_invitations: [...prevResult.client_invitations, ...nextResult.client_invitations],
        };

        return { ...prev, getClientInvitationsByClient: updatedResult };
      },
    });
  }

  return (
    <GetClientInvitationsByClientComponent
      notifyOnNetworkStatusChange
      variables={variables}
      {...queryProps}>
      {queryResult => (
        <QueryManager loader={loader} {...queryResult}>
          {({ data }) => {
            if (!data?.getClientInvitationsByClient) {
              return null;
            }

            return children(data.getClientInvitationsByClient, {
              loading: queryResult.loading,
              exhausted: !data.getClientInvitationsByClient.paginate,
              onLoadMore: () => handleLoadMore(queryResult),
            });
          }}
        </QueryManager>
      )}
    </GetClientInvitationsByClientComponent>
  );
};

export default GetClientInvitationsByClient;
